import { Component, computed, input, output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CommunicationsPropertySelectorComponent, PropertySelectionState }
  from '../communications-property-selector/communications-property-selector.component';
import { CommunicationsMemberSelectorComponent, MemberSelectionState }
  from '../communications-member-selector/communications-member-selector.component';
import { SelectedRecipient } from '../../../../core/models/ui/selected-recipient.model';

export enum RecipientMode {
  Members = 'members',
  Properties = 'properties'
}

// Recipients are mutually exclusive (members XOR properties). Only the state for the active mode is ever
// populated — switching mode destroys the other selector (via @if) and clears its stored state.
export interface RecipientSelection {
  mode: RecipientMode;
  memberState: MemberSelectionState | null;
  propertyState: PropertySelectionState | null;
  selectedCount: number;
  isValid: boolean;
}

@Component({
  selector: 'ms-recipient-selector',
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    CommunicationsPropertySelectorComponent,
    CommunicationsMemberSelectorComponent
  ],
  templateUrl: './recipient-selector.component.html',
  styleUrl: './recipient-selector.component.scss'
})
export class RecipientSelectorComponent {

  public organizationId = input.required<number>();

  public selectionChanged = output<RecipientSelection>();

  public RecipientMode = RecipientMode;
  public mode = signal<RecipientMode>(RecipientMode.Members);

  private memberState = signal<MemberSelectionState | null>(null);
  private propertyState = signal<PropertySelectionState | null>(null);

  // Only the active mode's selector is rendered (@if), so at most one of these is defined at a time.
  @ViewChild(CommunicationsMemberSelectorComponent) private memberSelector?: CommunicationsMemberSelectorComponent;
  @ViewChild(CommunicationsPropertySelectorComponent) private propertySelector?: CommunicationsPropertySelectorComponent;

  //#region Display state for the selected-recipients panel

  private activeState = computed(() => this.mode() === RecipientMode.Members ? this.memberState() : this.propertyState());

  public recipientTypeLabel = computed(() => this.mode() === RecipientMode.Members ? 'members' : 'properties');
  public activeSelectedCount = computed(() => this.activeState()?.selectedCount ?? 0);
  public isSelectAllActive = computed(() => this.activeState()?.useFilter ?? false);
  // In select-all mode these are the EXCLUDED recipients; otherwise the explicitly selected ones.
  public selectedRecipients = computed<SelectedRecipient[]>(() => this.activeState()?.selectedItems ?? []);

  public isExpanded = signal(false);

  public collapsedSummary = computed(() => {
    if (this.isSelectAllActive()) {
      return `All ${this.activeSelectedCount()} ${this.recipientTypeLabel()} matching the current filter`;
    }
    return this.selectedRecipients().map(r => r.label).join(', ');
  });

  public toggleExpanded(): void {
    this.isExpanded.update(v => !v);
  }

  //#endregion Display state

  public setMode(mode: RecipientMode): void {
    if (this.mode() === mode) {
      return;
    }

    // Clear the state for whichever mode we're leaving so a stale selection can never bleed across.
    this.memberState.set(null);
    this.propertyState.set(null);

    this.mode.set(mode);
    this.emitSelection();
  }

  public onMemberSelectionChanged(state: MemberSelectionState): void {
    this.memberState.set(state);
    this.emitSelection();
  }

  public onPropertySelectionChanged(state: PropertySelectionState): void {
    this.propertyState.set(state);
    this.emitSelection();
  }

  public removeRecipient(id: number): void {
    if (this.mode() === RecipientMode.Members) {
      this.memberSelector?.deselect(id);
    } else {
      this.propertySelector?.deselect(id);
    }
  }

  public reset(): void {
    this.memberSelector?.resetSelection();
    this.propertySelector?.resetSelection();
    this.memberState.set(null);
    this.propertyState.set(null);
    this.mode.set(RecipientMode.Members);
    this.emitSelection();
  }

  private emitSelection(): void {
    const isMembers = this.mode() === RecipientMode.Members;
    const activeState = isMembers ? this.memberState() : this.propertyState();

    this.selectionChanged.emit({
      mode: this.mode(),
      memberState: this.memberState(),
      propertyState: this.propertyState(),
      selectedCount: activeState?.selectedCount ?? 0,
      isValid: activeState?.isValid ?? false
    });
  }
}
